/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Shared per-person workload + earnings aggregation — ONE source of truth so the
 * Team Workload tab, the Portfolio Overview "by person" panel, and the public
 * profile's "Work & contribution" card all count identically:
 *
 *  - a task allocation = every assignee on a REAL task (its %responsible splits ∪
 *    the owner), excluding `milestone` / `phase` marker rows. Mirrors the Dashboard
 *    workload views (taskAssignees) so counts reconcile across every screen.
 *  - earnings = effectiveEarning(base, split) per compensation split (mirrors the
 *    Compensation page), bucketed into received vs still-outstanding.
 *
 * Pass `projectIds` to scope to a subset (e.g. the filtered portfolio); omit it to
 * count across every project.
 */
import { effectiveEarning } from '@heros/shared';

export interface PersonWork {
  /** Number of real-task allocations the person holds (work frequency). */
  tasks: number;
  /** Distinct projects the person works on (task assignee ∪ compensation recipient). */
  projects: Set<string>;
  /** tasks-per-job-category histogram (what jobs they do). */
  cats: Record<string, number>;
  /** Per-category breakdown: task count, how many they LEAD (major) vs assist (minor),
   *  and the Σ of their %responsibility — the "weighted" view next to the raw count. */
  catStats: Record<string, { count: number; major: number; minor: number; sumPct: number }>;
  /** Total compensation earned (Σ effectiveEarning). */
  earned: number;
  /** Earned portion already marked received. */
  received: number;
  /** Earned but not yet received. */
  outstanding: number;
}

export interface PersonWorkRow extends PersonWork {
  /** The team member this row aggregates. */
  u: any;
}

/** Everyone allocated to a task = its %responsible split members ∪ the owner. */
export function assigneesOf(t: { user?: string; splits?: Array<{ user?: string }> }): string[] {
  const ids = new Set<string>((t.splits ?? []).map((s) => s.user).filter((u): u is string => !!u));
  if (t.user) ids.add(t.user);
  return [...ids];
}

export function blankWork(): PersonWork {
  return { tasks: 0, projects: new Set<string>(), cats: {}, catStats: {}, earned: 0, received: 0, outstanding: 0 };
}

/** Milestone / phase rows are timeline markers, not real work. */
const isWorkRow = (t: any): boolean => t.rowType !== 'milestone' && t.rowType !== 'phase';

/** Largest single %responsibility held by any one assignee on a task (the lead share). */
export function taskTopPct(t: any): number {
  let max = 0;
  for (const uid of assigneesOf(t)) { const p = pctForUser(t, uid); if (p > max) max = p; }
  return max;
}

/**
 * MAJOR = this member "did the most" on the task: their %responsibility is at the top
 * share, ties at the top included (50/50 → both major). A 1-point tolerance applies ONLY
 * when 3+ people share the task, to absorb even-split rounding (34/33/33 → all three major)
 * — with just 2 people splits are exact, so 51/49 stays a clean major/minor. Else MINOR.
 */
export function isMajorOnTask(t: any, userId: string): boolean {
  const ids = assigneesOf(t);
  const top = taskTopPct(t);
  if (top <= 0) return false;
  const tol = ids.length >= 3 ? 1 : 0;
  return pctForUser(t, userId) + tol >= top;
}

export interface WorkInput {
  timeline: any[];
  compensations: any[];
  team: any[];
  /** Limit to these project ids; omit / null counts every project. */
  projectIds?: Set<string> | null;
}

/**
 * Aggregate per-person task allocations + earnings.
 * Returns `byUser` (keyed map, only people who have something) and `rows` (one row
 * per team member, blank-filled — stable for tables/charts).
 */
export function computePersonWork({ timeline, compensations, team, projectIds }: WorkInput): {
  byUser: Record<string, PersonWork>;
  rows: PersonWorkRow[];
} {
  const teamIds = new Set(team.map((u) => u.id));
  const inScope = (pid: string) => !projectIds || projectIds.has(pid);
  const byUser: Record<string, PersonWork> = {};
  const ensure = (uid: string): PersonWork => (byUser[uid] ??= blankWork());

  // (a) Task allocations — count every known assignee on each real work row, and tally
  //     the major/minor split + Σ%responsibility per job category (the weighted view).
  timeline.forEach((t) => {
    if (!inScope(t.project) || !isWorkRow(t)) return;
    const cat = t.category || 'Uncategorised';
    for (const uid of assigneesOf(t)) {
      if (!teamIds.has(uid)) continue; // known members only, so column/row totals reconcile
      const r = ensure(uid);
      r.tasks += 1; r.projects.add(t.project); r.cats[cat] = (r.cats[cat] || 0) + 1;
      const st = (r.catStats[cat] ??= { count: 0, major: 0, minor: 0, sumPct: 0 });
      st.count += 1; st.sumPct += pctForUser(t, uid);
      if (isMajorOnTask(t, uid)) st.major += 1; else st.minor += 1;
    }
  });

  // (b) Compensation earnings — a recipient also "touches" the project.
  compensations.forEach((c) => {
    if (!inScope(c.projectId)) return;
    (c.splits ?? []).forEach((sp: any) => {
      if (!teamIds.has(sp.user)) return;
      const share = effectiveEarning(c.base, sp);
      const r = ensure(sp.user);
      r.earned += share; r.projects.add(c.projectId);
      if (sp.received) r.received += share; else r.outstanding += share;
    });
  });

  const rows: PersonWorkRow[] = team.map((u) => ({ u, ...(byUser[u.id] ?? blankWork()) }));
  return { byUser, rows };
}

/** The member's %responsibility on a single task row (their split %, else owner = 100). */
export function pctForUser(t: { user?: string; splits?: Array<{ user?: string; pct?: number }> }, userId: string): number {
  const splits = t.splits ?? [];
  const sp = splits.find((s) => s.user === userId);
  if (sp) return Math.round(Number(sp.pct) || 0);
  // Owner of a genuinely split-less task → sole responsibility (mirrors how such a task is
  // normalised to [{ user, pct: 100 }]). But if splits EXIST yet exclude the owner — e.g. a
  // Compensation re-allocation rewrote splitsJson without touching userId — the owner no
  // longer holds 100%, so don't claim it (their card would otherwise read a misleading 100%).
  if (t.user === userId && splits.length === 0) return 100;
  return 0;
}

export interface CatProjectDetail {
  projectId: string;
  /** Every real-work task allocation in this (member, category, project) cell. `major` =
   *  the member led that task (top %responsibility share); else they assisted (minor). */
  tasks: Array<{ id: string; name: string; pct: number; major: boolean }>;
  /** Allocations here (== tasks.length) — this project's share of the cell's N. */
  count: number;
}

/**
 * The per-project breakdown BEHIND one matrix cell — every real-work task the given
 * member holds in the given category, grouped by project, each stamped with the
 * member's %responsibility. Σ count across the returned rows equals the cell's N in
 * `computePersonWork`, so the deck-card expander always reconciles with the table.
 * Scope with `projectIds` exactly as computePersonWork does (same `inScope` gate).
 */
export function personCategoryBreakdown(
  timeline: any[],
  userId: string,
  category: string,
  projectIds?: Set<string> | null,
): CatProjectDetail[] {
  const inScope = (pid: string) => !projectIds || projectIds.has(pid);
  const byProject = new Map<string, CatProjectDetail>();
  timeline.forEach((t) => {
    if (!isWorkRow(t) || !inScope(t.project)) return;
    if ((t.category || 'Uncategorised') !== category) return;
    if (!assigneesOf(t).includes(userId)) return;
    const d: CatProjectDetail = byProject.get(t.project) ?? { projectId: t.project, tasks: [], count: 0 };
    d.tasks.push({ id: t.id, name: t.name || '(untitled task)', pct: pctForUser(t, userId), major: isMajorOnTask(t, userId) });
    d.count += 1;
    byProject.set(t.project, d);
  });
  return [...byProject.values()];
}

/**
 * projectId → set of user ids who WORK ON it (task assignee ∪ compensation recipient).
 * Built over ALL projects (filter-independent) so it can drive a "this person's
 * projects" filter without a circular dependency on the current filter set.
 * Pass `teamIds` to count known members only — matching computePersonWork's gating so
 * the two stay consistent (a non-team split id never widens a project's toucher set).
 */
export function buildProjectTouchers(timeline: any[], compensations: any[], teamIds?: Set<string>): Map<string, Set<string>> {
  const m = new Map<string, Set<string>>();
  const touch = (pid: string, uid?: string) => {
    if (!uid || (teamIds && !teamIds.has(uid))) return;
    const set = m.get(pid) ?? new Set<string>();
    set.add(uid); m.set(pid, set);
  };
  timeline.forEach((t) => { if (isWorkRow(t)) for (const uid of assigneesOf(t)) touch(t.project, uid); });
  compensations.forEach((c) => (c.splits ?? []).forEach((sp: any) => touch(c.projectId, sp.user)));
  return m;
}

/**
 * Order job categories for a matrix: canonical `categories` order first (only those
 * actually used), then any extras alphabetically. `used` is the set/record of
 * categories that have ≥1 task across the rows in view.
 */
export function orderCategories(categories: Record<string, any>, used: Record<string, number>): string[] {
  const known = Object.keys(categories).filter((c) => used[c]);
  const extra = Object.keys(used).filter((c) => !(c in categories)).sort();
  return [...known, ...extra];
}
